import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { validatePasswordStrength, logPasswordChange } from '../utils/passwordUtils';

interface PasswordState {
  password: string;
  updatePassword: (newPassword: string) => boolean;
  validatePassword: (attempt: string) => boolean;
  resetPassword: () => void;
}

const DEFAULT_PASSWORD = 'today2024';

export const usePasswordStore = create<PasswordState>()(
  persist(
    (set, get) => ({
      password: DEFAULT_PASSWORD,
      
      updatePassword: (newPassword: string) => {
        try {
          const validation = validatePasswordStrength(newPassword);
          if (!validation.isValid) {
            logPasswordChange(false, validation.message);
            return false;
          }

          // Atomic update of both store and localStorage
          const updateStorage = () => {
            localStorage.setItem('app-password', newPassword);
            localStorage.setItem('app-password-direct', newPassword);
            set({ password: newPassword });
          };

          updateStorage();

          // Verify the update was successful
          const storedPassword = localStorage.getItem('app-password');
          const directPassword = localStorage.getItem('app-password-direct');
          const storePassword = get().password;

          const isUpdateSuccessful = 
            storedPassword === newPassword && 
            directPassword === newPassword && 
            storePassword === newPassword;

          if (!isUpdateSuccessful) {
            logPasswordChange(false, 'Password verification failed');
            return false;
          }

          logPasswordChange(true, 'Password updated successfully');
          return true;
        } catch (error) {
          logPasswordChange(false, error instanceof Error ? error.message : 'Unknown error');
          return false;
        }
      },

      validatePassword: (attempt: string) => {
        try {
          const storedPassword = localStorage.getItem('app-password');
          const directPassword = localStorage.getItem('app-password-direct');
          const storePassword = get().password;

          // Ensure all storage locations are in sync
          if (storedPassword !== directPassword || storedPassword !== storePassword) {
            // Use the store password as source of truth
            localStorage.setItem('app-password', storePassword);
            localStorage.setItem('app-password-direct', storePassword);
          }

          return attempt === storePassword;
        } catch (error) {
          console.error('Password validation error:', error);
          return false;
        }
      },

      resetPassword: () => {
        try {
          localStorage.setItem('app-password', DEFAULT_PASSWORD);
          localStorage.setItem('app-password-direct', DEFAULT_PASSWORD);
          set({ password: DEFAULT_PASSWORD });
          logPasswordChange(true, 'Password reset to default');
        } catch (error) {
          logPasswordChange(false, 'Failed to reset password');
        }
      }
    }),
    {
      name: 'app-password',
      storage: createJSONStorage(() => localStorage),
      version: 4,
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Ensure all storage locations are in sync on rehydration
          const password = state.password;
          localStorage.setItem('app-password', password);
          localStorage.setItem('app-password-direct', password);
        }
      }
    }
  )
);